// Chores — adult partner-share chart. Distinct surface from the kid
// Chores Settings panel; adults open this from the top-level nav and
// tick things off as they go.
//
// MR 7a ships the Today subview + New-chore sheet. Rotation + All
// views land in MR 7b / 7c respectively.
//
// ADHD-friendly framing baked into the UI per the design handoff:
// no streaks, no red urgency (amber for overdue under a calm
// "Behind · whenever you can" header), no toast on done beyond a
// quiet "Done", kind empty states.

import { useEffect, useRef, useState } from "preact/hooks";
import "../../styles/chores.css";

import { Icon } from "../../icons";
import { api, type AdultChore, type HouseholdMember, type PendingApproval } from "../../lib/rpc";
import { confirm } from "../../components/confirm";
import { TodayChores } from "./today";
import { FamilyStarsBar } from "./family-stars-bar";
import { KidsTodaySection } from "./kids-today";
import { NewChoreSheet } from "./new-chore-sheet";
import { ApprovalsBanner } from "./approvals";
import { AllChores } from "./all";
import { RewardsView } from "./rewards";
import { RotationView } from "./rotation";
import "./rotation.css";
import { BankView } from "./bank";

interface ChoresViewProps {
  notify: (msg: string) => void;
}

type View = "today" | "rotation" | "all" | "rewards" | "bank";

export function ChoresView({ notify }: ChoresViewProps) {
  const [chores, setChores] = useState<AdultChore[] | null>(null);
  // The Approvals banner needs the kid pending-approval queue; the
  // All view fetches its own unified payload (kid + adult).
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [view, setView] = useState<View>("today");
  const [newOpen, setNewOpen] = useState(false);
  // When set, the NewChoreSheet renders in edit mode pre-filled from
  // this chore. Cleared on save/cancel. The sheet is the same component
  // either way — `editingAdultChore` flips create→update inside.
  const [editingChore, setEditingChore] = useState<AdultChore | null>(null);
  // Kid-chore edit (from All view). Separate state so the sheet
  // disambiguates which RPC to call on save.
  const [editingKidChore, setEditingKidChore] = useState<{
    rawId: number; name: string; icon: string | null;
    assignee: string | null;
    assignmentMode: "single" | "rotating" | "parallel";
    assignees: string[]; frequency: string; stars: number; requiresApproval: boolean;
  } | null>(null);
  // Bumped on every mutation so the All view (which fetches its own
  // unified list) refetches alongside the Today view. Cheaper than
  // hoisting the unified list to the parent — keeps subviews
  // self-contained.
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = async () => {
    try {
      const r = await api.householdsList();
      const active = r.households.find((h) => h.id === r.activeHouseholdId);
      const m = active ? await api.householdsListMembers(active.id) : [];
      // Today view shows adult chores; Approvals banner shows kid
      // pending-approval queue; All view fetches its own unified
      // payload. Pull these in parallel so one slow store doesn't
      // gate the others.
      const [adultList, pending] = await Promise.all([
        api.adultChoresList(),
        api.choresPendingApprovals().catch(() => [] as PendingApproval[]),
      ]);
      setMembers(m);
      setChores(adultList);
      setPendingApprovals(pending);
    } catch (e: any) {
      notify(e?.message || "couldn't load chores");
      setChores([]);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const onSaved = async (kind: "kid" | "adult") => {
    const wasEditing = !!editingChore || !!editingKidChore;
    setNewOpen(false);
    setEditingChore(null);
    setEditingKidChore(null);
    await refresh();
    setRefreshToken((n) => n + 1);
    notify(
      wasEditing
        ? "Saved"
        : (kind === "kid" ? "Kid chore added" : "Added"),
    );
  };

  const onEdit = (chore: AdultChore) => {
    setEditingChore(chore);
    setEditingKidChore(null);
    setNewOpen(true);
  };

  // Edit dispatch from the unified All view — branches by kind so the
  // same sheet can edit either kid or adult chores.
  const onEditUnified = (c: { kind: "kid" | "adult"; rawId: number; name: string; icon: string | null; assignee: string | null; assignmentMode: "single" | "rotating" | "parallel"; assignees: string[]; frequency: string; stars: number; requiresApproval: boolean }) => {
    if (c.kind === "kid") {
      setEditingChore(null);
      setEditingKidChore({
        rawId: c.rawId,
        name: c.name,
        icon: c.icon,
        assignee: c.assignee,
        assignmentMode: c.assignmentMode,
        assignees: c.assignees,
        frequency: c.frequency,
        stars: c.stars,
        requiresApproval: c.requiresApproval,
      });
      setNewOpen(true);
      return;
    }
    // Adult — pull the full row from the parent's cached list so the
    // sheet has the AdultChore-typed object with hue/category/etc.
    const full = chores?.find((ac) => ac.id === c.rawId) || null;
    if (full) {
      setEditingChore(full);
      setEditingKidChore(null);
      setNewOpen(true);
    }
  };

  const onApprove = async (a: PendingApproval) => {
    try {
      await api.choresApprove(a.completionId);
      notify(`Approved · ⭐ ${a.choreStars} to ${a.completedBy}`);
      await refresh();
      setRefreshToken((n) => n + 1);
    } catch (e: any) { notify(e?.message || "couldn't approve"); }
  };

  const onDeny = async (a: PendingApproval, reason: string | null) => {
    try {
      await api.choresDeny(a.completionId, reason || undefined);
      notify(`Denied · ${a.completedBy} can redo`);
      await refresh();
      setRefreshToken((n) => n + 1);
    } catch (e: any) { notify(e?.message || "couldn't deny"); }
  };

  const onDone = async (chore: AdultChore) => {
    try {
      const r = await api.adultChoresMarkDone(chore.id);
      // No "Great job!" copy — just a quiet "Done" per ADHD-friendly
      // framing. Stars-awarded shown only when the chore has them set.
      notify(r.starsAwarded ? `Done · ⭐ +${r.starsAwarded}` : "Done");
      await refresh();
      setRefreshToken((n) => n + 1);
    } catch (e: any) { notify(e?.message || "couldn't mark done"); }
  };

  const onDelete = async (chore: AdultChore) => {
    const ok = await confirm({
      title: `Delete "${chore.name}"?`,
      message: "Removes the chore and its completion history.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.adultChoresDelete(chore.id);
      notify("Deleted");
      await refresh();
      setRefreshToken((n) => n + 1);
    } catch (e: any) { notify(e?.message || "couldn't delete"); }
  };

  return (
    <div className="chores-view">
      <header className="chores-head">
        <div>
          <div className="eyebrow">Chores</div>
          <h1 className="chores-title">
            Who's doing <span className="chores-title-accent">what.</span>
          </h1>
        </div>
        <div className="chores-head-right">
          <SegPill
            view={view}
            onPick={setView}
          />
          <button
            className="btn btn-primary chores-new-btn"
            type="button"
            onClick={() => setNewOpen(true)}
          >
            <Icon name="plus" size={13} />
            <span className="chores-new-label">New chore</span>
          </button>
        </div>
      </header>

      {chores === null && (
        <div className="chores-loading">loading…</div>
      )}

      {chores !== null && (
        <FamilyStarsBar refreshToken={refreshToken} notify={notify} />
      )}

      {chores !== null && pendingApprovals.length > 0 && (
        <ApprovalsBanner pending={pendingApprovals} onApprove={onApprove} onDeny={onDeny} />
      )}

      {chores !== null && view === "today" && (
        <>
          <TodayChores
            chores={chores}
            members={members}
            onDone={onDone}
            onEdit={onEdit}
            onDelete={onDelete}
          />
          <KidsTodaySection
            refreshToken={refreshToken}
            pending={pendingApprovals}
            onApprove={onApprove}
            onDeny={onDeny}
          />
        </>
      )}

      {chores !== null && view === "rotation" && (
        <RotationView refreshToken={refreshToken} />
      )}

      {chores !== null && view === "all" && (
        <AllChores
          members={members}
          notify={notify}
          refreshToken={refreshToken}
          onMutated={() => { void refresh(); }}
          onEdit={onEditUnified}
        />
      )}

      {chores !== null && view === "rewards" && (
        <RewardsView notify={notify} />
      )}

      {chores !== null && view === "bank" && (
        <BankView
          refreshToken={refreshToken}
          notify={notify}
          onMutated={() => { setRefreshToken((n) => n + 1); }}
        />
      )}

      {newOpen && (
        <NewChoreSheet
          members={members}
          onCancel={() => { setNewOpen(false); setEditingChore(null); setEditingKidChore(null); }}
          onSaved={onSaved}
          editingAdultChore={editingChore}
          editingKidChore={editingKidChore}
        />
      )}
    </div>
  );
}

// ── Segmented pill ───────────────────────────────────────────────
// Uses the existing pill-seg / pill-indicator pattern so the chrome
// matches the Food sub-nav. We position the indicator from the
// active button's bounds rather than a fixed left/width — the
// labels are localized-friendly and may grow with translation.

interface SegPillProps {
  view: View;
  onPick: (v: View) => void;
}

function SegPill({ view, onPick }: SegPillProps) {
  // Sliding indicator matches the Food pill — reposition under the
  // active button on view change AND on resize so the underline
  // stays aligned when the viewport reflows.
  const segRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState({ left: 4, width: 0 });
  useEffect(() => {
    const reposition = () => {
      const seg = segRef.current;
      if (!seg) return;
      const active = seg.querySelector('[data-active="true"]') as HTMLElement | null;
      if (!active) return;
      const segRect = seg.getBoundingClientRect();
      const r = active.getBoundingClientRect();
      setIndicator({ left: r.left - segRect.left, width: r.width });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [view]);

  return (
    <div className="pill-seg" role="tablist" aria-label="Chores view" ref={segRef}>
      <span className="pill-indicator" style={{ left: indicator.left, width: indicator.width }} />
      {/* Labels wrapped in <span> so the @media (max-width: 520px)
        * rule in shell.css can hide them and collapse to icon-only —
        * matches the Food pill's behavior on narrow viewports. */}
      <button
        type="button"
        role="tab"
        data-active={view === "today"}
        onClick={() => onPick("today")}
        title="Today"
      >
        <Icon name="home" size={14} /> <span>Today</span>
      </button>
      <button
        type="button"
        role="tab"
        data-active={view === "rotation"}
        onClick={() => onPick("rotation")}
        title="Rotation"
      >
        <Icon name="share" size={14} /> <span>Rotation</span>
      </button>
      <button
        type="button"
        role="tab"
        data-active={view === "all"}
        onClick={() => onPick("all")}
        title="All"
      >
        <Icon name="memory" size={14} /> <span>All</span>
      </button>
      <button
        type="button"
        role="tab"
        data-active={view === "rewards"}
        onClick={() => onPick("rewards")}
        title="Rewards"
      >
        <Icon name="star" size={14} /> <span>Rewards</span>
      </button>
      <button
        type="button"
        role="tab"
        data-active={view === "bank"}
        onClick={() => onPick("bank")}
        title="Bank"
      >
        <Icon name="dollar" size={14} /> <span>Bank</span>
      </button>
    </div>
  );
}
